import { useEffect } from 'react';
import { useStore } from 'zustand';
import { useReactFlow, useStoreApi } from '@xyflow/react';
import { useDependencias } from '../../application/dependencias';
import { cameraNoFoco } from '../../domain/camera';

/** Pedido sem post-it na tela (excluído, sem acesso) expira para não puxar a tela depois, de surpresa. */
const VALIDADE_PEDIDO_MS = 15_000;
const PULSO: Keyframe[] = [
  { boxShadow: '0 0 0 0 rgba(37, 99, 235, 0.75)' },
  { boxShadow: '0 0 0 18px rgba(37, 99, 235, 0)' },
];

/**
 * Aviso aberto no sino (RQ-NOT-011): quando o post-it pedido está entre os nós deste quadro, a tela vai a 75% de zoom
 * com ele a 2/5 da largura e da altura, ele pulsa por alguns segundos e o pedido é atendido.
 */
export function useFocoNaNota(nos: { id: string }[]) {
  const { storeNotificacoes } = useDependencias();
  const uuid = useStore(storeNotificacoes, (s) => s.notaEmFoco);
  const { getInternalNode, setViewport } = useReactFlow();
  const motor = useStoreApi();
  const id = uuid && nos.some((n) => n.id === `n:${uuid}`) ? `n:${uuid}` : null;

  useEffect(() => {
    if (!uuid) return;
    const expirar = setTimeout(() => storeNotificacoes.getState().notaEmFoco === uuid && storeNotificacoes.getState().focarNota(null), VALIDADE_PEDIDO_MS);
    return () => clearTimeout(expirar);
  }, [uuid, storeNotificacoes]);

  useEffect(() => {
    if (!id) return;
    // Depois do primeiro desenho: a câmera inicial do quadro recém-aberto não pode sobrepor o enquadramento.
    const espera = setTimeout(() => {
      const no = getInternalNode(id);
      const { width, height } = motor.getState();
      if (no) {
        const item = { ...no.internals.positionAbsolute, largura: no.measured.width ?? no.width ?? 0, altura: no.measured.height ?? no.height ?? 0 };
        void setViewport(cameraNoFoco(item, { largura: width, altura: height }), { duration: 600 });
        document.querySelector(`.react-flow__node[data-id="${id}"] > .nota`)?.animate(PULSO, { duration: 800, iterations: 3 });
      }
      storeNotificacoes.getState().focarNota(null);
    }, 120);
    return () => clearTimeout(espera);
  }, [id, getInternalNode, setViewport, motor, storeNotificacoes]);
}
